import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companiesService } from '../services/companies.service';
import type {
  CompanyFilters,
  CompanyScope,
  PaginationParams,
  CreateCompanyDto,
  UpdateCompanyDto,
} from '../types/company.types';

const COMPANIES_KEY = 'companies';
const COMPANY_KEY = 'company';
const COMPANY_STATS_KEY = 'company-stats';

/**
 * Hook pour récupérer la liste paginée des companies
 * @param scope 'mine' par défaut (buyers CRM). 'tenants' / 'all' réservé super_admin.
 */
export function useCompanies(
  filters: CompanyFilters = {},
  pagination: Partial<PaginationParams> = {},
  scope?: CompanyScope,
) {
  return useQuery({
    queryKey: [COMPANIES_KEY, filters, pagination, scope ?? 'mine'],
    queryFn: () => companiesService.getAll(filters, pagination, scope),
  });
}

/**
 * Hook pour récupérer une company par son ID
 */
export function useCompany(id: number | undefined) {
  return useQuery({
    queryKey: [COMPANY_KEY, id],
    queryFn: () => companiesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook pour récupérer les statistiques CRM d'une company (opportunités liées)
 */
export function useCompanyStats(id: number | undefined) {
  return useQuery({
    queryKey: [COMPANY_STATS_KEY, id],
    queryFn: () => companiesService.getStats(id!),
    enabled: !!id,
  });
}

/**
 * Hook pour créer une company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCompanyDto) => companiesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] });
    },
  });
}

/**
 * Hook pour créer une company depuis le SIRET via INSEE.
 * scope='mine' (défaut) = buyer CRM ; scope='tenants' = tenant BPA (super_admin).
 */
export function useCreateCompanyFromSiret() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      siret,
      companyType,
      scope,
    }: {
      siret: string;
      companyType?: string;
      scope?: CompanyScope;
    }) => companiesService.createFromSiret(siret, companyType, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] });
    },
  });
}

/**
 * Hook pour mettre à jour une company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCompanyDto }) =>
      companiesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_STATS_KEY, id] });
    },
  });
}

/**
 * Hook pour supprimer une company
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => companiesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMPANIES_KEY] });
    },
  });
}
